import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static targets = ["partnersList", "partnerRow", "addButton", "totalEquity", "totalEquityValue", "totalEquityStatus", "equityChart", "submitButton"]

  connect() {
    console.log("Fund partners form controller connected")
    this.lockedPartners = new Set() // Track which partners have locked percentages
    this.chart = null // Will hold the ApexCharts instance

    // Store all available member options from the first select on page load
    const firstSelect = this.partnersListTarget.querySelector('select[name*="[member_id]"]')
    if (firstSelect) {
      this.allMemberOptions = Array.from(firstSelect.options).map(opt => ({
        value: opt.value,
        text: opt.text
      }))
    }

    this.updateAddButtonState()
    this.updateTotalEquity()
    this.updateEquityChart()
    this.updateSubmitButtonState()
  }

  disconnect() {
    // Clean up chart when controller disconnects
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  addPartnerRow() {
    console.log("Add partner row clicked")
    const tbody = this.partnersListTarget

    // Count visible rows
    const visibleRows = Array.from(tbody.querySelectorAll('tr')).filter(row => row.style.display !== 'none')
    const totalPartners = this.allMemberOptions.filter(opt => opt.value !== '').length

    // Don't allow more rows than available partners
    if (visibleRows.length >= totalPartners) {
      console.log("Maximum number of partner rows reached")
      return
    }

    const newIndex = new Date().getTime()

    // Get all currently selected member IDs to exclude them from the new dropdown
    const selectedMemberIds = new Set()
    tbody.querySelectorAll('select[name*="[member_id]"]').forEach(select => {
      // Only include visible rows (not ones marked for destruction)
      const row = select.closest('tr')
      if (row && row.style.display !== 'none' && select.value) {
        selectedMemberIds.add(select.value)
      }
    })

    // Filter out already-selected partners from stored options
    let optionsHTML = ''
    this.allMemberOptions.forEach(option => {
      if (option.value === '' || !selectedMemberIds.has(option.value)) {
        optionsHTML += `<option value="${option.value}">${option.text}</option>`
      }
    })

    const row = document.createElement('tr')
    row.className = 'border-b dark:border-gray-700'
    row.setAttribute('data-fund-partners-form-target', 'partnerRow')
    row.setAttribute('data-row-id', newIndex) // Add unique row ID

    row.innerHTML = `
      <td class="px-4 py-3">
        <select name="fund[fund_partners_attributes][${newIndex}][member_id]"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                data-action="change->fund-partners-form#partnerChanged"
                required>
          ${optionsHTML}
        </select>
      </td>
      <td class="px-4 py-3">
        <select name="fund[fund_partners_attributes][${newIndex}][role]"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
          <option value="lead">Lead</option>
          <option value="advisor">Advisor</option>
        </select>
      </td>
      <td class="px-4 py-3">
        <div class="flex items-center space-x-2">
          <input type="number"
                 name="fund[fund_partners_attributes][${newIndex}][equity_percentage]"
                 step="0.01"
                 min="0"
                 max="100"
                 placeholder="e.g., 50.00"
                 data-action="input->fund-partners-form#equityChanged"
                 class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
          <button type="button"
                  class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  data-action="click->fund-partners-form#toggleLock"
                  title="Lock percentage">
            <svg class="w-5 h-5 lock-icon hidden" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
            </svg>
            <svg class="w-5 h-5 unlock-icon" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"></path>
            </svg>
          </button>
        </div>
      </td>
      <td class="px-4 py-3">
        <button type="button"
                class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                data-action="click->fund-partners-form#removePartnerRow">
          Remove
        </button>
      </td>
    `

    tbody.appendChild(row)
    this.updateAddButtonState()
    this.autoDistributeEquity() // Auto-distribute when adding new partner

    // Ensure chart updates even if auto-distribute doesn't run
    this.updateEquityChart()
  }

  updateTotalEquity() {
    if (!this.hasTotalEquityTarget) return

    const tbody = this.partnersListTarget
    let total = 0

    // Sum all equity percentages from visible rows
    tbody.querySelectorAll('input[name*="[equity_percentage]"]').forEach(input => {
      const row = input.closest('tr')
      // Only include visible rows (not ones marked for destruction)
      if (row && row.style.display !== 'none' && input.value) {
        total += parseFloat(input.value) || 0
      }
    })

    // Round to 2 decimal places
    total = Math.round(total * 100) / 100

    // Update the display
    this.totalEquityValueTarget.textContent = `${total.toFixed(2)}%`

    // Update styling based on whether it equals 100%
    const tolerance = 0.01 // Within 0.01% is considered "perfect"

    if (Math.abs(total - 100) < tolerance) {
      // Perfect - equals 100%
      this.totalEquityTarget.className = 'px-4 py-3 font-semibold bg-green-50 dark:bg-green-900/20'
      this.totalEquityValueTarget.className = 'text-green-900 dark:text-green-100'
      this.totalEquityStatusTarget.textContent = '✓ Perfect'
      this.totalEquityStatusTarget.className = 'ml-2 text-xs text-green-600 dark:text-green-400'
    } else if (total === 0) {
      // No equity assigned yet
      this.totalEquityTarget.className = 'px-4 py-3 font-semibold bg-gray-50 dark:bg-gray-700'
      this.totalEquityValueTarget.className = 'text-gray-900 dark:text-gray-100'
      this.totalEquityStatusTarget.textContent = '(Not assigned)'
      this.totalEquityStatusTarget.className = 'ml-2 text-xs text-gray-500 dark:text-gray-400'
    } else if (total < 100) {
      // Under 100%
      this.totalEquityTarget.className = 'px-4 py-3 font-semibold bg-yellow-50 dark:bg-yellow-900/20'
      this.totalEquityValueTarget.className = 'text-yellow-900 dark:text-yellow-100'
      this.totalEquityStatusTarget.textContent = `↓ ${(100 - total).toFixed(2)}% remaining`
      this.totalEquityStatusTarget.className = 'ml-2 text-xs text-yellow-700 dark:text-yellow-400'
    } else {
      // Over 100%
      this.totalEquityTarget.className = 'px-4 py-3 font-semibold bg-red-50 dark:bg-red-900/20'
      this.totalEquityValueTarget.className = 'text-red-900 dark:text-red-100'
      this.totalEquityStatusTarget.textContent = `↑ ${(total - 100).toFixed(2)}% over`
      this.totalEquityStatusTarget.className = 'ml-2 text-xs text-red-700 dark:text-red-400'
    }
  }

  updateAddButtonState() {
    if (!this.hasAddButtonTarget) return
    if (!this.allMemberOptions) return

    const tbody = this.partnersListTarget

    // Count total available partners (excluding the blank option) from stored options
    const totalPartners = this.allMemberOptions.filter(opt => opt.value !== '').length

    // Count visible rows
    const visibleRows = Array.from(tbody.querySelectorAll('tr')).filter(row => row.style.display !== 'none')

    // Count currently selected partners in visible rows
    const selectedMemberIds = new Set()
    tbody.querySelectorAll('select[name*="[member_id]"]').forEach(select => {
      const row = select.closest('tr')
      if (row && row.style.display !== 'none' && select.value) {
        selectedMemberIds.add(select.value)
      }
    })

    // Disable button if we've reached max rows OR all partners are selected
    if (visibleRows.length >= totalPartners || selectedMemberIds.size >= totalPartners) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.addButtonTarget.title = 'All available partners have been added'
    } else {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      this.addButtonTarget.title = ''
    }
  }

  removePartnerRow(event) {
    const row = event.target.closest('tr')
    const rowId = row.dataset.rowId

    // Remove from locked partners if it was locked
    if (rowId && this.lockedPartners.has(rowId)) {
      this.lockedPartners.delete(rowId)
    }

    // Check if this row has a hidden _destroy field (for existing records)
    const destroyField = row.querySelector('.destroy-field')
    if (destroyField) {
      // Mark for destruction and hide instead of removing
      destroyField.value = '1'
      row.style.display = 'none'
    } else {
      // New row that hasn't been saved - just remove it
      row.remove()
    }

    // Update dropdowns, button state, recalculate equity, and update chart
    this.updatePartnerDropdowns()
    this.updateAddButtonState()
    this.autoDistributeEquity()
    this.updateSubmitButtonState()
  }

  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return

    const hasSelectedPartner = this.hasAtLeastOnePartner()

    if (hasSelectedPartner) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      this.submitButtonTarget.title = ''
    } else {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.submitButtonTarget.title = 'Select at least one partner to continue'
    }
  }

  hasAtLeastOnePartner() {
    const tbody = this.partnersListTarget
    const rows = Array.from(tbody.querySelectorAll('tr')).filter(row => row.style.display !== 'none')

    return rows.some(row => {
      const select = row.querySelector('select[name*="[member_id]"]')
      return select && select.value && select.value !== ''
    })
  }

  // Call this when a partner selection changes
  partnerChanged(event) {
    this.updateAddButtonState()
    this.updatePartnerDropdowns()
    this.updateSubmitButtonState()

    // Check if any partners have equity assigned
    const tbody = this.partnersListTarget
    const rows = Array.from(tbody.querySelectorAll('tr')).filter(row => row.style.display !== 'none')
    const hasAnyEquity = rows.some(row => {
      const input = row.querySelector('input[name*="[equity_percentage]"]')
      return input && parseFloat(input.value) > 0
    })

    // If no equity is assigned yet, auto-distribute when a partner is selected
    if (!hasAnyEquity) {
      const row = event.target.closest('tr')
      const select = row.querySelector('select[name*="[member_id]"]')

      // Only auto-distribute if a valid partner was selected
      if (select && select.value) {
        this.autoDistributeEquity()
      }
    } else {
      // Otherwise just update the chart
      this.updateEquityChart()
    }
  }

  // Update all partner dropdowns to remove already-selected options
  updatePartnerDropdowns() {
    if (!this.allMemberOptions) return

    const tbody = this.partnersListTarget
    const rows = Array.from(tbody.querySelectorAll('tr')).filter(row => row.style.display !== 'none')

    // Get all currently selected member IDs
    const selectedMemberIds = new Set()
    rows.forEach(row => {
      const select = row.querySelector('select[name*="[member_id]"]')
      if (select && select.value) {
        selectedMemberIds.add(select.value)
      }
    })

    // Update each dropdown
    rows.forEach(row => {
      const select = row.querySelector('select[name*="[member_id]"]')
      if (!select) return

      const currentValue = select.value

      // Clear and rebuild options
      select.innerHTML = ''

      this.allMemberOptions.forEach(option => {
        // Include the option if it's blank, currently selected in this row, or not selected anywhere else
        if (option.value === '' || option.value === currentValue || !selectedMemberIds.has(option.value)) {
          const optElement = document.createElement('option')
          optElement.value = option.value
          optElement.text = option.text
          select.appendChild(optElement)
        }
      })

      // Restore the selected value
      select.value = currentValue
    })
  }

  // Call this when equity percentage changes
  equityChanged() {
    this.updateTotalEquity()
    this.updateEquityChart()
  }

  // Toggle lock state for a partner's equity percentage
  toggleLock(event) {
    const row = event.target.closest('tr')
    const rowId = row.dataset.rowId
    const lockButton = event.target.closest('button')
    const lockIcon = lockButton.querySelector('.lock-icon')
    const unlockIcon = lockButton.querySelector('.unlock-icon')

    if (this.lockedPartners.has(rowId)) {
      // Unlock
      this.lockedPartners.delete(rowId)
      lockButton.classList.remove('text-primary-600', 'dark:text-primary-400')
      lockButton.classList.add('text-gray-400', 'dark:text-gray-500')
      lockIcon.classList.add('hidden')
      unlockIcon.classList.remove('hidden')
      lockButton.title = 'Lock percentage'
    } else {
      // Lock
      this.lockedPartners.add(rowId)
      lockButton.classList.remove('text-gray-400', 'dark:text-gray-500')
      lockButton.classList.add('text-primary-600', 'dark:text-primary-400')
      lockIcon.classList.remove('hidden')
      unlockIcon.classList.add('hidden')
      lockButton.title = 'Unlock percentage'
    }
  }

  // Auto-distribute equity among unlocked partners
  autoDistributeEquity() {
    const tbody = this.partnersListTarget
    const rows = Array.from(tbody.querySelectorAll('tr')).filter(row => row.style.display !== 'none')

    // Count unlocked partners
    const unlockedRows = rows.filter(row => !this.lockedPartners.has(row.dataset.rowId))
    const unlockedCount = unlockedRows.length

    if (unlockedCount === 0) return // All locked, nothing to distribute

    // Calculate total locked percentage
    let lockedTotal = 0
    rows.forEach(row => {
      if (this.lockedPartners.has(row.dataset.rowId)) {
        const input = row.querySelector('input[name*="[equity_percentage]"]')
        lockedTotal += parseFloat(input.value) || 0
      }
    })

    // Distribute remaining percentage among unlocked partners
    const remainingPercentage = 100 - lockedTotal

    // Use integer division - give base amount to all, remainder to first partner
    const basePercentage = Math.floor(remainingPercentage / unlockedCount)
    const remainder = remainingPercentage - (basePercentage * unlockedCount)

    unlockedRows.forEach((row, index) => {
      const input = row.querySelector('input[name*="[equity_percentage]"]')
      // First partner gets the remainder
      const percentage = index === 0 ? basePercentage + remainder : basePercentage
      input.value = percentage.toFixed(2)
    })

    this.updateTotalEquity()
    this.updateEquityChart()
  }

  updateEquityChart() {
    if (!this.hasEquityChartTarget) {
      console.log("No equity chart target found")
      return
    }

    const tbody = this.partnersListTarget
    const rows = Array.from(tbody.querySelectorAll('tr')).filter(row => row.style.display !== 'none')

    // Collect data for chart
    const chartData = []
    const labels = []

    rows.forEach(row => {
      const select = row.querySelector('select[name*="[member_id]"]')
      const input = row.querySelector('input[name*="[equity_percentage]"]')
      const value = parseFloat(input.value) || 0

      if (value > 0) {
        // Show partner name if selected, otherwise show "Unassigned Partner"
        const partnerName = (select && select.value)
          ? select.options[select.selectedIndex].text
          : 'Unassigned Partner'
        labels.push(partnerName)
        chartData.push(value)
      }
    })

    console.log("Chart data:", chartData, "Labels:", labels)

    // If no data, destroy chart and hide container
    if (chartData.length === 0) {
      console.log("No data for chart, hiding")
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
      this.equityChartTarget.parentElement.parentElement.style.display = 'none'
      return
    }

    // Show container
    console.log("Showing chart")
    this.equityChartTarget.parentElement.parentElement.style.display = 'block'

    // Create or update chart
    if (this.chart) {
      // Update existing chart
      console.log("Updating existing chart with data:", chartData, "labels:", labels)
      this.chart.updateOptions({
        labels: labels
      }, false, false)
      this.chart.updateSeries(chartData)
    } else {
      console.log("Creating new chart with data:", chartData, "labels:", labels)
      // Create new ApexCharts pie chart
      const options = {
        series: chartData,
        chart: {
          type: 'pie',
          height: 350,
          width: '100%',
          fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif'
        },
        labels: labels,
        colors: [
          '#3b82f6',  // blue
          '#10b981',  // green
          '#f59e0b',  // amber
          '#ef4444',  // red
          '#8b5cf6',  // purple
          '#ec4899',  // pink
          '#14b8a6',  // teal
          '#fb923c',  // orange
        ],
        legend: {
          position: 'right',
          fontSize: '13px',
          width: 300,
          labels: {
            colors: '#6b7280'
          },
          offsetY: 0,
          offsetX: 0,
          itemMargin: {
            vertical: 4
          }
        },
        plotOptions: {
          pie: {
            offsetX: -20,
            dataLabels: {
              offset: -15
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            // val is the percentage that ApexCharts calculates from the series
            // We want to show the actual equity percentage from our data instead
            return opts.w.config.series[opts.seriesIndex].toFixed(1) + "%"
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val.toFixed(2) + "%"
            }
          }
        }
      }

      this.chart = new ApexCharts(this.equityChartTarget, options)
      this.chart.render()
    }
  }
}
